import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandHeart, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYouWall() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to: "", message: "" });

  const load = () => axios.get(`${API}/thankyous`).then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.to.trim() || !form.message.trim()) return toast.error("Both fields are required");
    await axios.post(`${API}/thankyous`, form);
    toast.success("Thank you note posted! 💜");
    setForm({ to: "", message: "" });
    setOpen(false);
    load();
  };

  return (
    <section id="thankyou" className="py-24 md:py-32 bg-zinc-900/50 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <p className="font-sub text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-3">Gratitude Corner</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase">
            Thank You <span className="text-purple-400">Notes</span>
          </h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="thankyou-open-btn"
                className="flex items-center gap-2 font-sub font-semibold border border-purple-500/50 hover:bg-purple-600 text-white px-6 py-3 rounded-full transition-colors duration-200 active:scale-95"
              >
                <HandHeart className="w-4 h-4" /> Chase Says Thanks
              </button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-sub">Post a Thank You (Chase's corner)</DialogTitle>
                <DialogDescription className="text-zinc-400">Say thanks for a gift that arrived.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <Input data-testid="thankyou-to-input" placeholder="Who are you thanking?" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="bg-zinc-800 border-white/10" />
                <Textarea data-testid="thankyou-message-input" placeholder="Your thank you message..." rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-zinc-800 border-white/10" />
                <Button data-testid="thankyou-submit-btn" onClick={submit} className="w-full bg-purple-600 hover:bg-purple-500 font-sub font-semibold">
                  <Send className="w-4 h-4 mr-2" /> Post Thank You
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {notes.length === 0 ? (
          <p className="text-zinc-500 font-sub" data-testid="thankyou-empty-state">
            Chase's thank you notes will appear here as gifts start arriving. 🙏
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="thankyou-list">
            {notes.map((n) => (
              <div key={n.id} data-testid={`thankyou-note-${n.id}`} className="rounded-2xl bg-zinc-950 border border-white/5 hover:border-purple-500/40 p-8 transition-colors duration-300">
                <HandHeart className="w-6 h-6 text-purple-400 mb-4" />
                <p className="font-sub text-zinc-200 leading-relaxed mb-4">"{n.message}"</p>
                <p className="font-sub text-sm text-purple-300 font-semibold">To: {n.to}</p>
                <p className="font-sub text-xs text-zinc-500 mt-1">— Chase</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
